/**
 * Agent Repository
 * 
 * Handles database operations for agents using the consolidated agents table.
 * Part of the 3-layer architecture (Repository → Service → React Query Hooks)
 */

import { supabase } from '../supabase';
import { BaseRepository } from './BaseRepository';

export type AgentStatus = 'available' | 'busy' | 'away' | 'break' | 'meal' | 'meeting' | 'training' | 'offline';

export interface Agent {
  id: string;
  user_id: string;
  name: string;
  email: string;
  phone_number: string | null;
  current_status: AgentStatus;
  skills: string[] | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AgentWithQueues extends Agent {
  queues: Array<{
    queue_id: string;
    queue_name: string;
    company_name: string;
    priority: number;
  }>;
}

const normalizeAgentStatus = (s: any): AgentStatus => {
  const key = (s ?? '').toString().toLowerCase();
  const legacyMap: Record<string, AgentStatus> = {
    on_call: 'busy',
    wrap_up: 'busy',
    online: 'available',
    idle: 'available',
  };
  if (!key || key === 'offline') return 'available';
  return (legacyMap[key] ?? (s as AgentStatus));
};

export class AgentRepository extends BaseRepository<Agent> {
  constructor() {
    super({ tableName: 'agents', primaryKey: 'id' });
  }

  /**
   * Get agent by user_id (for current logged-in user)
   */
  async getByUserId(userId: string): Promise<AgentWithQueues | null> {
    try {
      // Get agent record
      const { data: agent, error: agentError } = await supabase
        .from(this.config.tableName)
        .select('*')
        .eq('user_id', userId)
        .single();

      if (agentError) {
        if (agentError.code === 'PGRST116') return null; // Not found
        throw agentError;
      }

      if (!agent) return null;

      // Get queue assignments
      const { data: queueAssignments, error: queuesError } = await supabase
        .from('agent_queue_assignments')
        .select(`
          queue_id,
          priority,
          call_queues!inner (
            id,
            name,
            company_name
          )
        `)
        .eq('agent_id', agent.id);

      if (queuesError) {
        console.error('[AgentRepository] Error fetching queue assignments:', queuesError);
      }

      // Format queue data
      const queues = (queueAssignments || []).map((assignment: any) => ({
        queue_id: assignment.queue_id,
        queue_name: assignment.call_queues?.name || 'Unknown',
        company_name: assignment.call_queues?.company_name || 'Unknown',
        priority: assignment.priority || 0,
      }));

      // Normalize status (map legacy values and default missing/offline to 'available')
      const normalizedAgent = {
        ...(agent as any),
        current_status: normalizeAgentStatus((agent as any)?.current_status),
      } as Agent;

      return {
        ...normalizedAgent,
        queues,
      };
    } catch (error) {
      console.error('[AgentRepository] getByUserId error:', error);
      throw error;
    }
  }

  /**
   * Update agent status
   */
  async updateStatus(userId: string, status: AgentStatus): Promise<Agent | null> {
    try {
      const { data, error } = await supabase
        .from(this.config.tableName)
        .update({ 
          current_status: status,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[AgentRepository] updateStatus error:', error);
      throw error;
    }
  }

  /**
   * Get all active agents
   */
  async getActiveAgents(): Promise<Agent[]> {
    try {
      const { data, error } = await supabase
        .from(this.config.tableName)
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      // Default agents by normalizing status (map legacy values and default missing/offline to 'available')
      const normalized = (data || []).map((a: any) => ({
        ...a,
        current_status: normalizeAgentStatus(a?.current_status),
      }));

      return normalized;
    } catch (error) {
      console.error('[AgentRepository] getActiveAgents error:', error);
      throw error;
    }
  }

  /**
   * Subscribe to agent status changes for a specific user
   */
  subscribeToAgentStatusChanges(
    userId: string,
    callback: (agent: Agent) => void
  ): () => void {
    const channel = supabase
      .channel(`agent_status_${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: this.config.tableName,
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const newAgent = payload.new as Agent;
          const normalized: Agent = {
            ...newAgent,
            current_status: normalizeAgentStatus(newAgent?.current_status),
          };
          callback(normalized);
        }
      )
      .subscribe();

    // Return cleanup function
    return () => {
      supabase.removeChannel(channel);
    };
  }
}
